// Cross-tab event bus over a single BroadcastChannel. Messages are delivered only
// to *other* browsing contexts on the same origin (never the sender), which is exactly
// what cross-tab sync needs. Feature-detected so it no-ops where BroadcastChannel is absent.
//
// Event names + channel name: `constants/broadcastEvents`. Payload types: `types/broadcastEvents`.

import { BROADCAST_CHANNEL_NAME } from 'constants/broadcastEvents';
import { BroadcastEvent } from 'types/broadcastEvents';

const isSupported = typeof BroadcastChannel !== 'undefined';

let channel: BroadcastChannel | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (!isSupported) return null;
  if (!channel) {
    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
};

export const publishBroadcast = (event: BroadcastEvent): void => {
  try {
    getChannel()?.postMessage(event);
  } catch {
    // Ignore — a failed broadcast should never break the originating action.
  }
};

export const subscribeBroadcast = (
  handler: (event: BroadcastEvent) => void
): (() => void) => {
  const ch = getChannel();
  if (!ch) return () => {};

  const listener = (e: MessageEvent<BroadcastEvent>) => handler(e.data);
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
};
